import { Injectable, signal } from '@angular/core';
import { Observable, first, map, mergeMap, retry, tap } from 'rxjs';
import { backendUrl } from './global-storage';
import { HttpClient } from '@angular/common/http';
import { OrganizationDetails, OrganizationShortDetails, OrganizationsListResponse } from '../model/model';
import { UtilsService } from './utils';

@Injectable({
  providedIn: 'root',
})
export class UserOrganizationsService {
  public readonly cachedOrganizations = signal<OrganizationShortDetails[]>([]);

  private organizationsObservable?: Observable<OrganizationShortDetails[]>;

  /**
   * ewusia i tatusi
   */
  constructor(
    private http: HttpClient,
    private utils: UtilsService,
  ) {}

  refreshCachedOrganizations() {
    backendUrl
      .pipe(
        mergeMap((url) => this.http.get<OrganizationsListResponse>(`${url}/orgs`)),
        retry(2),
        first(),
      )
      .subscribe({
        next: (v) => {
          this.cachedOrganizations.set(v.organizations ?? []);
        },
        error: this.utils.showHttpError,
      });
  }

  listUserOrganizations() {
    if (this.organizationsObservable == null) {
      this.organizationsObservable = backendUrl.pipe(
        mergeMap((url) => this.http.get<OrganizationsListResponse>(`${url}/orgs`)),
        map((resp) => resp.organizations ?? []),
      );
    }

    return this.organizationsObservable;
  }

  create(value: OrganizationDetails) {
    return backendUrl.pipe(
      mergeMap((url) => this.http.post(`${url}/orgs`, value)),
      tap(() => this.refreshCachedOrganizations()),
    );
  }

  update(value: OrganizationDetails) {
    return backendUrl.pipe(
      mergeMap((url) => this.http.put(`${url}/orgs/${value.orgId}`, value)),
      tap(() => this.refreshCachedOrganizations()),
    );
  }

  listOrganizationMembers(orgId: string) {
    return backendUrl.pipe(mergeMap((url) => this.http.get<OrganizationMembersResponse>(`${url}/orgs/${orgId}/users`)));
  }

  setOrganizationMember(orgId: string, member: OrganizationMemberAddRequest) {
    return backendUrl.pipe(mergeMap((url) => this.http.post(`${url}/orgs/${orgId}/users`, member)));
  }

  removeOrganizationMember(orgId: string, userId: string) {
    return backendUrl.pipe(mergeMap((url) => this.http.delete(`${url}/orgs/${orgId}/users/${userId}`)));
  }

  protected mapOrgs(userOrgs: OrganizationsListResponse): UserOrganization[] {
    const userOrgsArray = userOrgs.organizations ?? [];
    const mappedUserOrgs = userOrgsArray
      .map<UserOrganization>((o) => ({
        id: o.orgId,
        name: o.name,
        role: 'user',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return mappedUserOrgs;
  }
}

export const USER_ORG_LOCAL_DATA: unique symbol = Symbol();

export interface UserOrganization {
  id: string;
  name: string;
  role: 'admin' | 'user';

  [USER_ORG_LOCAL_DATA]?: {
    updatable?: boolean;
  };
}

export interface OrganizationMember {
  userId: string;
  role: string;
  email: string | null;
}

export interface OrganizationMemberAddRequest {
  userId?: string | null;
  role: string;
  email?: string | null;
}

export interface OrganizationMembersResponse {
  members: OrganizationMember[];
}
